(ns lupapalvelu.ui.admin.asiointi-api.registrations
  (:require [lupapalvelu.asiointi.notifications.registration-schemas :as registration]
            [lupapalvelu.next.event :refer [<sub >evt]]
            [lupapalvelu.next.util :as nutil]
            [lupapalvelu.ui.admin.asiointi-api.common :as asiointi-common]
            [lupapalvelu.ui.common :as common]
            [lupapalvelu.ui.components :as components]
            [lupapalvelu.ui.components.input :as ui-input]
            [lupapalvelu.ui.components.reselect :as reselect]
            [lupapalvelu.ui2.sortable-table :as sortable-table]
            [malli.core :as m]
            [malli.transform :as malli-transform]
            [re-frame.core :as rf]
            [sade.strings :as ss]
            [sade.util :as util]))

(def tab-id :admin.asiointiapi.registration.tab)

(defn- cleanup-fx
  [{:keys [db]} _]
  {:db (dissoc db ::data)})

(defn- set-sort-fx
  [{:keys [db]} [_ sort-key sort-dir start limit]]
  {:db (update db ::data util/assoc-when
               :sort-key sort-key
               :sort-dir sort-dir
               :start start
               :limit limit)})

(def key-to-field
  {:admin.asiointiapi.registration.created-at      :created-at
   :admin.asiointiapi.registration.organization-id :organization-id
   :admin.asiointiapi.registration.owner-id        :owner-username})

(def dir-to-val
  {:asc  1
   :desc -1})

(def default-sort-key :admin.asiointiapi.registration.created-at)
(def default-sort-dir :asc)
(def default-sort-start 0)
(def default-sort-limit 20)

(defn- registrations-query-fx
  [{{{:keys [sort-key sort-dir start limit]} ::data} :db} [_]]
  {:fx [[:action/query {:name    :registrations-with-statistics
                        :params  (util/strip-nils
                                   {:sort-key (some-> sort-key key-to-field)
                                    :sort-dir (some-> sort-dir dir-to-val)
                                    :start    start
                                    :limit    limit})
                        :success [::registrations-with-statistics.success]}]]})

(defn- query-with-sorting-fx
  [{:keys [db] :as cofx} [_ sort-key sort-dir start limit]]
  {:pre [(keyword? sort-key) (keyword? sort-dir) (int? start) (int? limit)]}
  ;; The `sortable-table` triggers `:get-rows` when the table renders for the
  ;; first time. But we have already fetched the data, so don't re-run the query
  ;; unless the search conditions change
  (let [get-values (juxt :sort-key :sort-dir :start :limit)
        current    (get-values (::data db))]
    (when (not= current [sort-key sort-dir start limit])
      (nutil/merge-every-fn cofx [[set-sort-fx sort-key sort-dir start limit]
                                  [registrations-query-fx]]))))

(rf/reg-event-fx ::sort
  query-with-sorting-fx)

(def statistics-coercer! (m/coercer registration/ApiRegistrationWithStatsWithAuthCheck
                                    malli-transform/json-transformer))

(rf/reg-event-fx ::registrations-with-statistics.success
  (fn [{:keys [db]} [_ {:keys [registrations total-count]}]]
    {:db (update db ::data assoc
                 ;; Use coercer for proper keywords and sets etc.
                 :registrations (mapv statistics-coercer! registrations)
                 :total-count total-count)}))

(defn- db->registration
  [db registration-id]
  (->> db ::data :registrations (util/find-by-id registration-id)))

(defn on-selected-fx
  "Invoked when a AsiointiAPI tab is selected"
  [cofx [_ selected-tab]]
  (nutil/merge-every-fn cofx
                        (if (= tab-id selected-tab)
                          [[query-with-sorting-fx default-sort-key default-sort-dir default-sort-start default-sort-limit]]
                          [[cleanup-fx]])))

(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::total-count
  :<- [::data]
  :-> :total-count)

(rf/reg-sub ::registrations
  :<- [::data]
  :-> (comp not-empty :registrations))

(rf/reg-sub ::registration
  :<- [::registrations]
  (fn [registrations [_ registration-id]]
    (util/find-by-id registration-id registrations)))

(rf/reg-sub ::owner-choices
  :<- [::asiointi-common/users]
  (fn [users]
    (->> users
         vals
         (filter (every-pred (comp seq :orgAuthz) :enabled))
         (map (fn [user]
                {:text  (:username user)
                 :value (:_id user)}))
         (into []))))

(rf/reg-sub ::organization-id-choices
  (fn [[_ registration-id]]
    {:pre [registration-id]}
    [(rf/subscribe [::current-owner-id registration-id])
     (rf/subscribe [::asiointi-common/users])])
  (fn [[owner-id users]]
    ;; Given a selected user, check which organizations
    ;; they have access to.
    (->> (get users owner-id)
         :orgAuthz
         (filter (fn [[_org-id roles]]
                   (some #{:api-reader :api-writer} roles)))
         (map key)
         (into #{}))))

(rf/reg-sub ::organization-choices
  (fn [[_ registration-id]]
    {:pre [registration-id]}
    [(rf/subscribe [::organization-id-choices registration-id])
     (rf/subscribe [::asiointi-common/organizations])])
  (fn [[organization-ids organizations]]
    (let [lang (keyword (common/get-current-language))]
      (->> (select-keys organizations organization-ids)
           (map (fn [[org-id data]]
                  {:value org-id
                   :text  (str (name org-id) ": " (get data lang))}))
           (sort-by :text)
           (into [])))))

(rf/reg-sub ::edits
  :<- [::data]
  :-> :edits)

(defn- db->edits
  [db]
  (->> db ::data :edits))

(rf/reg-event-fx ::edit-owner-id
  (fn [{:keys [db]} [_ owner-id]]
    {:db (-> db
             (assoc-in [::data :edits :owner-id] owner-id)
             ;; Organization depends on the owner -> reset when owner changes
             (update-in [::data :edits] dissoc :organization-id))}))

(rf/reg-event-fx ::edit-organization-id
  (fn [{:keys [db]} [_ organization-id]]
    {:db (assoc-in db [::data :edits :organization-id] organization-id)}))

(rf/reg-event-fx ::edit-auth-type
  (fn [{:keys [db]} [_ auth-type]]
    {:db (assoc-in db [::data :edits :auth-type] auth-type)
     ;; A bit of a hack: changing the auth type will display different fields
     ;; on the UI. The legacy JS modal doesn't react to this automatically, so we
     ;; must manually poke the dialog to resize it.
     :fx [[:dialog/resize]]}))

(rf/reg-event-fx ::edit-auth-none-url
  (fn [{:keys [db]} [_ url]]
    {:db (assoc-in db [::data :edits :auth-none :url] url)}))

(rf/reg-event-fx ::edit-mfiles-username
  (fn [{:keys [db]} [_ username]]
    {:db (assoc-in db [::data :edits :auth-mfiles :username] username)}))

(rf/reg-event-fx ::edit-mfiles-password
  (fn [{:keys [db]} [_ password]]
    {:db (assoc-in db [::data :edits :auth-mfiles :password] password)}))

(rf/reg-event-fx ::edit-mfiles-token-url
  (fn [{:keys [db]} [_ token-url]]
    {:db (assoc-in db [::data :edits :auth-mfiles :token-url] token-url)}))

(rf/reg-event-fx ::edit-mfiles-notify-url
  (fn [{:keys [db]} [_ notify-url]]
    {:db (assoc-in db [::data :edits :auth-mfiles :notify-url] notify-url)}))

(rf/reg-event-fx ::edit-mfiles-vault-guid
  (fn [{:keys [db]} [_ vault-guid]]
    {:db (assoc-in db [::data :edits :auth-mfiles :vault-guid] vault-guid)}))

(rf/reg-sub ::current-data
  (fn [[_ registration-id]]
    {:pre [registration-id]}
    (cond-> {:edits (rf/subscribe [::edits])}
      (not= ::new registration-id) (assoc :registration (rf/subscribe [::registration registration-id]))))
  (fn [{:keys [edits registration]}]
    (util/deep-merge registration edits)))

(rf/reg-sub ::current-owner-id
  (fn [[_ registration-id]]
    {:pre [registration-id]}
    (rf/subscribe [::current-data registration-id]))
  :-> :owner-id)

(rf/reg-sub ::current-organization-id
  (fn [[_ registration-id]]
    {:pre [registration-id]}
    (rf/subscribe [::current-data registration-id]))
  :-> :organization-id)

(rf/reg-sub ::current-auth-type
  (fn [[_ registration-id]]
    {:pre [registration-id]}
    (rf/subscribe [::current-data registration-id]))
  (fn [{:keys [auth-type]}]
    auth-type))

(rf/reg-sub ::current-auth-none-url
  (fn [[_ registration-id]]
    {:pre [registration-id]}
    (rf/subscribe [::current-data registration-id]))
  :-> (comp :url :auth-none))

(rf/reg-sub ::current-mfiles-username
  (fn [[_ registration-id]]
    {:pre [registration-id]}
    (rf/subscribe [::current-data registration-id]))
  :-> (comp :username :auth-mfiles))

(rf/reg-sub ::current-mfiles-password
  (fn [[_ registration-id]]
    {:pre [registration-id]}
    (rf/subscribe [::current-data registration-id]))
  :-> (comp :password :auth-mfiles))

(rf/reg-sub ::current-mfiles-vault-guid
  (fn [[_ registration-id]]
    {:pre [registration-id]}
    (rf/subscribe [::current-data registration-id]))
  :-> (comp :vault-guid :auth-mfiles))

(rf/reg-sub ::current-mfiles-token-url
  (fn [[_ registration-id]]
    {:pre [registration-id]}
    (rf/subscribe [::current-data registration-id]))
  :-> (comp :token-url :auth-mfiles))

(rf/reg-sub ::current-mfiles-notify-url
  (fn [[_ registration-id]]
    {:pre [registration-id]}
    (rf/subscribe [::current-data registration-id]))
  :-> (comp :notify-url :auth-mfiles))

(defn- db-clear-dialog-data-fx
  [{:keys [db]} _]
  {:db (update db ::data dissoc :edits :dialog-for)})

(rf/reg-sub ::dialog-visible?
  :<- [::data]
  (fn [data [_ registration-id]]
    (= registration-id (:dialog-for data))))

(defn- dialog-close-fx
  [_ _]
  {:fx [[:dialog/close]]})

(rf/reg-event-fx ::close-dialog
  (fn [cofx _]
    (nutil/merge-every-fn cofx [[db-clear-dialog-data-fx]
                                [dialog-close-fx]])))

(def coerce-new (m/coercer registration/ApiCreateRegistrationWithAuthCheck
                           malli-transform/strip-extra-keys-transformer
                           identity
                           (constantly nil)))

(rf/reg-sub ::can-submit-new-registration?
  :<- [::current-data ::new]
  (fn [{:keys [organization-id owner-id] :as current-data}]
    (boolean (and current-data
                  ;; coercer only checks actual registration payload
                  (ss/blank-as-nil organization-id)
                  (ss/blank-as-nil owner-id)
                  (coerce-new current-data)))))

(rf/reg-event-fx ::submit-new-registration
  (fn [{:keys [db]} _]
    (let [{:keys [owner-id
                  organization-id
                  auth-type auth-none auth-mfiles]} (-> db ::data :edits)]
      {:fx [[:action/command {:name    :create-notification-registration
                              :params  {:organizationId organization-id
                                        :ownerId        owner-id
                                        :registration   (util/assoc-when
                                                          {:auth-type auth-type}
                                                          :auth-none auth-none
                                                          :auth-mfiles auth-mfiles)}
                              :success [::create-notification-registration.success]}]]})))

(rf/reg-event-fx ::create-notification-registration.success
  (fn [cofx [_ _resp]]
    (nutil/merge-every-fn cofx [[db-clear-dialog-data-fx]
                                [registrations-query-fx]
                                [dialog-close-fx]])))

(def coerce (m/coercer registration/ApiModifyRegistrationAsAdmin
                       (malli-transform/transformer malli-transform/json-transformer
                                                    malli-transform/strip-extra-keys-transformer)
                       identity
                       (constantly nil)))

(def coerce-with-auth-check (m/coercer (registration/with-auth-check registration/ApiModifyRegistrationAsAdmin)
                                       (malli-transform/transformer malli-transform/json-transformer
                                                                    malli-transform/strip-extra-keys-transformer)
                                       identity
                                       (constantly nil)))

(rf/reg-sub ::can-submit-modified-registration?
  (fn [[_ registration-id]]
    [(rf/subscribe [::registration registration-id])
     (rf/subscribe [::current-data registration-id])
     (rf/subscribe [::edits registration-id])])
  (fn [[original modified edits]]
    (and (not-empty edits)
         (coerce edits)
         ;; Include auth-check to make sure the whole resulting
         ;; registration is ok, as `edits` might only contain a `auth-type` change.
         (not= (coerce-with-auth-check original)
               (coerce-with-auth-check modified)))))

(rf/reg-event-fx ::submit-modified-registration
  (fn [{:keys [db]} [_ registration-id]]
    (let [{:keys [organization-id id]} (db->registration db registration-id)
          changes                      (-> (db->edits db)
                                           (select-keys registration/admin-modifiable-fields)
                                           coerce)]
      {:fx [[:action/command {:name                  :modify-notification-registration
                              :params                {:organizationId organization-id
                                                      :registrationId id
                                                      :changes        changes}
                              :success               [::modify-notification-registration.success]
                              :show-saved-indicator? true}]]})))

(rf/reg-event-fx ::modify-notification-registration.success
  (fn [cofx [_ _resp]]
    (nutil/merge-every-fn cofx [[db-clear-dialog-data-fx]
                                [registrations-query-fx]
                                [dialog-close-fx]])))

(defn- owner-id-field
  [registration-id]
  [:<>
   [:label {:for "asiointi-owner-select"} (common/loc :admin.asiointiapi.registration.owner-id)]
   [reselect/reselect
    (<sub [::current-owner-id registration-id])
    {:id        "asiointi-owner-select"
     :multi?    false
     :clear?    true
     :disabled? (not= ::new registration-id)
     :items     (<sub [::owner-choices])
     :callback  (fn [owner-id] (>evt [::edit-owner-id owner-id]))}]])

(defn- organization-id-field
  [registration-id]
  (let [choices (<sub [::organization-choices registration-id])]
    [:<>
     [:label {:for "asiointi-organization-select"} (common/loc :admin.asiointiapi.registration.organization-id)]
     [reselect/reselect
      (<sub [::current-organization-id registration-id])
      {:id        "asiointi-organization-select"
       :multi?    false
       :clear?    true
       :disabled? (or (not= ::new registration-id)
                      (empty? choices))
       :items     choices
       :callback  (fn [organization-id] (>evt [::edit-organization-id organization-id]))}]]))

(defn- auth-type-id-field
  [registration-id]
  [:<>
   [:label {:for "asiointi-auth-type-select"} (common/loc :admin.asiointiapi.registration.auth-type)]
   [reselect/reselect
    (<sub [::current-auth-type registration-id])
    {:id        "asiointi-auth-type-select"
     :multi?    false
     :clear?    true
     :disabled? (or (nil? (<sub [::current-owner-id registration-id]))
                    (nil? (<sub [::current-organization-id registration-id])))
     :items     [{:value    :auth-none
                  :text-loc :admin.asiointiapi.registration.auth-type.auth-none}
                 {:value    :auth-mfiles
                  :text-loc :admin.asiointiapi.registration.auth-type.auth-mfiles}]
     :callback  (fn [auth-type] (>evt [::edit-auth-type auth-type]))}]])

(def loc-prefix :admin.asiointiapi.registration)

(defn- simple-text-field
  [{:keys [registration-id
           field-path
           placeholder?
           sub-id
           event-id
           info-loc]}]
  (let [id (ss/join "-" (map name field-path))]
    [:<>
     [:label {:for id}
      (common/loc (util/kw-path loc-prefix field-path :long))]
     (when info-loc
       [:flex.flex-row
        [:i.lupicon-circle-info.primary.mx-2
         {:aria-hidden true}]
        [:span.align-top (common/loc (util/kw-path loc-prefix info-loc))]])
     [ui-input/text-input
      (cond-> {:id        id
               :class     "lux w-full"
               :disabled? (or (nil? (<sub [::current-owner-id registration-id]))
                              (nil? (<sub [::current-organization-id registration-id])))
               :value     (<sub [sub-id registration-id])
               :on-change (fn [val] (>evt [event-id val]))}
        placeholder? (assoc :placeholder (common/loc (util/kw-path loc-prefix field-path :placeholder))))]]))

(defn- simple-password-field
  [{:keys [registration-id
           field-path
           sub-id
           event-id]}]
  (let [id (ss/join "-" (map name field-path))]
    [:<>
     [:label {:for id}
      (common/loc (util/kw-path loc-prefix field-path :long))]
     [ui-input/password-input
      {:id        id
       :class     "lux w-full"
       :disabled? (or (nil? (<sub [::current-owner-id registration-id]))
                      (nil? (<sub [::current-organization-id registration-id])))
       :value     (<sub [sub-id registration-id])
       :on-change (fn [val] (>evt [event-id val]))}]]))

(defn- auth-none-url-field
  [registration-id]
  [simple-text-field {:registration-id registration-id
                      :field-path      [:auth-none :url]
                      :placeholder?    true
                      :sub-id          ::current-auth-none-url
                      :event-id        ::edit-auth-none-url}])

(defn- mfiles-username-field
  [registration-id]
  [simple-text-field {:registration-id registration-id
                      :field-path      [:auth-mfiles :username]
                      :sub-id          ::current-mfiles-username
                      :event-id        ::edit-mfiles-username}])

(defn- mfiles-password-field
  [registration-id]
  [simple-password-field {:registration-id registration-id
                          :field-path      [:auth-mfiles :password]
                          :sub-id          ::current-mfiles-password
                          :event-id        ::edit-mfiles-password}])

(defn- mfiles-vault-guid-field
  [registration-id]
  [simple-text-field {:registration-id registration-id
                      :field-path      [:auth-mfiles :vault-guid]
                      :placeholder?    true
                      :sub-id          ::current-mfiles-vault-guid
                      :event-id        ::edit-mfiles-vault-guid}])

(defn- mfiles-token-url-field
  [registration-id]
  [simple-text-field {:registration-id registration-id
                      :field-path      [:auth-mfiles :token-url]
                      :placeholder?    true
                      :sub-id          ::current-mfiles-token-url
                      :event-id        ::edit-mfiles-token-url
                      :info-loc        :use-https}])

(defn- mfiles-notify-url-field
  [registration-id]
  [simple-text-field {:registration-id registration-id
                      :field-path      [:auth-mfiles :notify-url]
                      :placeholder?    true
                      :sub-id          ::current-mfiles-notify-url
                      :event-id        ::edit-mfiles-notify-url
                      :info-loc        :use-https}])

(defn- create-or-edit-modal
  [registration-id]
  ;; The modal system in Lupapiste seems to leave old modals in the DOM.
  ;; Use a visibility check to discard the contents & subscriptions
  (when (<sub [::dialog-visible? registration-id])
    (let [new? (= ::new registration-id)]
      [:div.dialog-content
       [:div.flex.flex-col.gap-2.m-2
        [:h3 (common/loc (if new?
                           :admin.asiointiapi.registration.add.title
                           :admin.asiointiapi.registration.edit.title))]
        [owner-id-field registration-id]
        [organization-id-field registration-id]
        [auth-type-id-field registration-id]
        (condp = (<sub [::current-auth-type registration-id])
          nil          nil
          :auth-none   [auth-none-url-field registration-id]
          :auth-mfiles [:<>
                        [mfiles-username-field registration-id]
                        [mfiles-password-field registration-id]
                        [mfiles-vault-guid-field registration-id]
                        [mfiles-token-url-field registration-id]
                        [mfiles-notify-url-field registration-id]])
        [:div.flex-row
         [:div.left
          (if new?
            [components/icon-button {:icon      :lupicon-check
                                     :class     :primary.close
                                     :text-loc  :add
                                     :disabled? (not (<sub [::can-submit-new-registration?]))
                                     :on-click  #(>evt [::submit-new-registration])}]
            [components/icon-button {:icon      :lupicon-check
                                     :class     :primary.close
                                     :text-loc  :save
                                     :disabled? (not (<sub [::can-submit-modified-registration? registration-id]))
                                     :on-click  #(>evt [::submit-modified-registration registration-id])}])]
         [:div.right
          [components/icon-button {:icon     :lupicon-remove
                                   :class    :secondary
                                   :text-loc :cancel
                                   :on-click #(>evt [::close-dialog])}]]]]])))

(defn- show-create-or-edit-dialog-fx
  [{:keys [db]} [_ registration-id]]
  {:db (-> db
           (update ::data dissoc :edits)
           (assoc-in [::data :dialog-for] registration-id))
   :fx [[:dialog/show {:ltitle           (if (= ::new registration-id)
                                           :admin.asiointiapi.registration.add
                                           :admin.asiointiapi.registration.edit)
                       :size             :large
                       :type             :react
                       :dialog-component [create-or-edit-modal registration-id]
                       :minContentHeight "20em"
                       :cancel-callback  #(>evt [::close-dialog])}]
        [:dialog/resize]]})

(rf/reg-event-fx ::add-clicked
  (fn [cofx _]
    (nutil/merge-every-fn cofx
                          [[show-create-or-edit-dialog-fx ::new]])))

(rf/reg-event-fx ::edit-clicked
  (fn [cofx [_ registration-id]]
    (nutil/merge-every-fn cofx
                          [[show-create-or-edit-dialog-fx registration-id]])))

(defn- copy-to-edits-fx
  [{:keys [db]} [_ registration-id]]
  {:pre [registration-id]}
  (let [{:keys [owner-id organization-id auth-none auth-mfiles auth-type]} (db->registration db registration-id)]
    {:db (assoc-in db [::data :edits] (util/strip-blanks
                                        {:owner-id        owner-id
                                         :organization-id organization-id
                                         :auth-type       auth-type
                                         :auth-none       auth-none
                                         :auth-mfiles     auth-mfiles}))}))

(rf/reg-event-fx ::copy-clicked
  (fn [cofx [_ registration-id]]
    (nutil/merge-every-fn cofx
                          [[show-create-or-edit-dialog-fx ::new]
                           [copy-to-edits-fx registration-id]])))

(rf/reg-event-fx ::remove-clicked
  (fn [{:keys [db]} [_ registration-id]]
    (let [{:keys [organization-id]} (db->registration db registration-id)]
      {:fx [[:dialog/show {:text  (common/loc :admin.asiointiapi.registration.remove)
                           :type  :yes-no
                           :event [::remove-registration-confirmed registration-id organization-id]}]]})))

(rf/reg-event-fx ::remove-registration-confirmed
  (fn [_ [_ registration-id organization-id]]
    {:fx [[:action/command {:name    :remove-notification-registration
                            :params  {:registrationId registration-id
                                      :organizationId organization-id}
                            :success [::remove-notification-registration.success]}]]}))

(rf/reg-event-fx ::remove-notification-registration.success
  registrations-query-fx)

(rf/reg-event-fx ::ping-clicked
  (fn [{:keys [db]} [_ registration-id]]
    (let [{:keys [organization-id]} (db->registration db registration-id)]
      {:fx [[:action/command {:name    :send-ping-to-registration
                              :params  {:registrationId registration-id
                                        :organizationId organization-id}
                              :success [::send-ping-to-registration.success]
                              :error   [::send-ping-to-registration.failure]}]]})))

(rf/reg-event-fx ::send-ping-to-registration.success
  (fn [_ _]
    {:fx [[:indicator/show {:text-loc :admin.asiointiapi.registration.ping.success
                            :style    :positive}]]}))

(rf/reg-event-fx ::send-ping-to-registration.failure
  (fn [_ [_ {:keys [result]}]]
    {:fx [[:indicator/show {:text  (common/loc :admin.asiointiapi.registration.ping.failure result)
                            ;; Would be nice to use `:negative`, but those are always sticky,
                            ;; which is too dramatic for this use case.
                            :style :primary}]]}))

(rf/reg-sub ::table-rows
  (fn [_]
    [(rf/subscribe [::registrations])
     (rf/subscribe [::asiointi-common/users])])
  (fn [[registrations users]]
    (->> registrations
         (mapv (fn [{:keys           [owner-id]
                     {done    true
                      pending false} :delivered
                     :as             registration}]
                 (let [user  (get users owner-id)
                       total (+ done pending)]
                   (-> registration
                       (dissoc :owner-id :delivered :ignored)
                       (assoc :owner-username (:username user)
                              :delivery-label (str (or done 0) "/" total))))))
         not-empty)))

(defn- actions-cell
  [registration-id]
  [:div.no-twp.flex.flex-wrap.gap-2
   [components/icon-button
    {:icon     :lupicon-pen
     :class    :primary
     :text-loc :edit
     :on-click #(>evt [::edit-clicked registration-id])}]
   ;; Enable copying to make it easier to add similar registrations
   ;; to multiple organizations
   [components/icon-button
    {:icon     :lupicon-copy
     :class    :secondary
     :text-loc :copy
     :on-click #(>evt [::copy-clicked registration-id])}]
   [components/icon-button
    {:icon     :lupicon-check
     :class    :secondary
     :text-loc :admin.asiointiapi.registration.ping
     :on-click #(>evt [::ping-clicked registration-id])}]
   [components/icon-button
    {:icon       :lupicon-trash
     :icon-only? true
     :class      :secondary
     :text-loc   :remove
     :on-click   #(>evt [::remove-clicked registration-id])}]])

(defn registration-table
  []
  [:div.no-twp
   [:h3 (common/loc :admin.asiointiapi.registration.table.header)]
   [components/icon-button
    {:icon     :lupicon-plus
     :class    :secondary.mb-2
     :text-loc :add
     :on-click #(>evt [::add-clicked])}]
   (if-let [rows (<sub [::table-rows])]
     [:div {:class ["nobase" "twp" "lp-theme"]}
      [sortable-table/table
       {:columns    [{:key      :admin.asiointiapi.registration.created-at
                      :get      :created-at
                      :sortable true}
                     {:key      :admin.asiointiapi.registration.owner-id
                      :get      :owner-username
                      :sortable true}
                     {:key      :admin.asiointiapi.registration.organization-id
                      :get      :organization-id
                      :sortable true}
                     {:key :admin.asiointiapi.registration.auth-type
                      :get (fn [{:keys [auth-type]}]
                             (common/loc (util/kw-path :admin.asiointiapi.registration.auth-type auth-type)))}
                     {:key :admin.asiointiapi.registration.delivered
                      :get :delivery-label}
                     {:key :admin.actions
                      :get (fn [{registration-id :id}]
                             [actions-cell registration-id])}]
        :get-row-id :id
        :get-rows   (fn [{[[sort-key sort-dir]] :sort
                          :keys                 [start limit]}]
                      (when (and sort-key sort-dir start limit)
                        (>evt [::sort sort-key sort-dir start limit])))
        :rows       rows
        :total-rows (<sub [::total-count])}]]
     [:p (common/loc :admin.asiointiapi.registration.none)])])
